import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Trash, MagnifyingGlass, ArrowsClockwise, Train, ArrowLeft } from "@phosphor-icons/react";

/* ═══════════════════════════════════════════════════════════════════════
 * RailConsignmentsList — full-detail list of every rail consignment
 * across every status, with wagon roll-up counters and (admin) delete.
 * Powers the /rail-consignments route and is linked from the Grindrod
 * Rail Coordinator dashboard, the Rail Operations Overview dashboard
 * and the Header main menu.
 * ═══════════════════════════════════════════════════════════════════════ */

const STATUS_OPTIONS = [
    "", "draft", "loading", "in_transit_to_transnet", "received_at_transnet",
    "staged_at_transnet", "in_transit_to_grindrod", "received_at_grindrod",
    "waiting_for_tipping", "tipping_in_progress", "completed",
    "empty_wagons_returning", "empty_wagons_at_transnet",
    "empty_wagons_in_transit_to_mine", "cycle_complete",
];

const fmtDt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-ZA", { hour12: false }); }
    catch { return iso; }
};

function statusPill(s) {
    const tones = {
        cycle_complete: "bg-emerald-100 text-emerald-800 border-emerald-300",
        completed:      "bg-emerald-100 text-emerald-800 border-emerald-300",
        tipping_in_progress: "bg-blue-100 text-blue-800 border-blue-300",
        waiting_for_tipping: "bg-blue-100 text-blue-800 border-blue-300",
        received_at_grindrod: "bg-blue-50 text-blue-700 border-blue-200",
        in_transit_to_grindrod: "bg-purple-100 text-purple-800 border-purple-300",
        staged_at_transnet: "bg-amber-100 text-amber-800 border-amber-300",
        received_at_transnet: "bg-amber-100 text-amber-800 border-amber-300",
        in_transit_to_transnet: "bg-orange-100 text-orange-800 border-orange-300",
        loading:  "bg-yellow-100 text-yellow-800 border-yellow-300",
        draft:    "bg-slate-100 text-slate-700 border-slate-300",
    };
    return (
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border ${tones[s] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
            {(s || "").replaceAll("_", " ")}
        </span>
    );
}

export default function RailConsignmentsList() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [statusF, setStatusF] = useState("");
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/rail/consignments", { params: { include_completed: true } });
            setRows(r.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const query = q.trim().toLowerCase();
        return (rows || []).filter((c) => {
            if (statusF && c.status !== statusF) return false;
            if (!query) return true;
            return [c.consignment_no, c.train_id, c.mine_name, c.client_name, c.client_mine, c.coal_grade]
                .filter(Boolean).some((v) => String(v).toLowerCase().includes(query));
        });
    }, [rows, q, statusF]);

    const remove = async (c) => {
        if (!window.confirm(`Delete consignment ${c.consignment_no}? This wipes the consignment, its events, alerts and notifications and cannot be undone.`)) return;
        setBusy(c.id);
        try {
            await api.delete(`/rail/consignments/${c.id}`);
            toast.success(`Deleted ${c.consignment_no}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <main className="max-w-[1700px] mx-auto px-3 md:px-6 py-4 space-y-4"
                  data-testid="rail-consignments-page">
                <div className="flex flex-wrap items-center gap-3">
                    <Link to="/rail-operations"
                          className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-[var(--grindrod-navy)]">
                        <ArrowLeft size={14} weight="bold" /> Rail Operations
                    </Link>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-xl md:text-2xl font-black uppercase tracking-wide text-[var(--grindrod-navy)] flex items-center gap-2">
                            <Train size={26} weight="fill" className="text-indigo-600" />
                            Rail Consignments
                        </h1>
                        <div className="text-[11px] text-slate-500 tracking-wide uppercase font-semibold">
                            {filtered.length} of {rows.length} · full wagon roll-up · every state{isAdmin ? " · admin can delete" : ""}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative">
                            <MagnifyingGlass size={14} weight="bold"
                                             className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="search" value={q} onChange={(e) => setQ(e.target.value)}
                                placeholder="Search consignment / train / mine / grade"
                                className="g-input !py-1 !pl-7 text-xs w-72"
                                data-testid="rail-list-search"
                            />
                        </div>
                        <select value={statusF} onChange={(e) => setStatusF(e.target.value)}
                                className="g-input !py-1 !px-2 text-xs w-auto"
                                data-testid="rail-list-status">
                            {STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>{s ? s.replaceAll("_", " ") : "All statuses"}</option>
                            ))}
                        </select>
                        <button onClick={load} className="px-2 py-1 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 text-[10px] font-black uppercase tracking-widest transition"
                                data-testid="rail-list-refresh"
                                title="Refresh">
                            <ArrowsClockwise size={14} weight="bold" />
                        </button>
                    </div>
                </div>

                <div className="g-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-[11px]" data-testid="rail-list-table">
                            <thead className="bg-slate-100 text-slate-700 uppercase text-[9px] tracking-widest">
                                <tr>
                                    <th className="text-left px-2 py-2">Consignment</th>
                                    <th className="text-left px-2 py-2">Train · Mine · Grade</th>
                                    <th className="text-right px-2 py-2">Total</th>
                                    <th className="text-right px-2 py-2">At Transnet</th>
                                    <th className="text-right px-2 py-2">At Grindrod</th>
                                    <th className="text-right px-2 py-2">Released</th>
                                    <th className="text-right px-2 py-2">Tipped</th>
                                    <th className="text-right px-2 py-2">Tons</th>
                                    <th className="text-left px-2 py-2">Status</th>
                                    <th className="text-left px-2 py-2">Created</th>
                                    <th className="text-left px-2 py-2">Completed</th>
                                    {isAdmin && <th className="text-center px-2 py-2">Delete</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {loading && !filtered.length && (
                                    <tr><td colSpan={isAdmin ? 12 : 11} className="py-6 text-center text-slate-400 italic">Loading…</td></tr>
                                )}
                                {!loading && !filtered.length && (
                                    <tr><td colSpan={isAdmin ? 12 : 11} className="py-6 text-center text-slate-400 italic">
                                        No consignments match the current filter.
                                    </td></tr>
                                )}
                                {filtered.map((c) => (
                                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50"
                                        data-testid={`rail-list-row-${c.consignment_no}`}>
                                        <td className="px-2 py-1.5 font-mono font-bold">{c.consignment_no}</td>
                                        <td className="px-2 py-1.5">
                                            <div className="font-semibold text-slate-800">{c.train_id || "—"}</div>
                                            <div className="text-slate-500 text-[10px]">
                                                {c.mine_name || c.client_mine || "—"}
                                                {c.coal_grade ? ` · ${c.coal_grade}` : ""}
                                            </div>
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-mono">{c.wagon_count || 0}</td>
                                        <td className="px-2 py-1.5 text-right font-mono">
                                            {Math.max(0, (c.wagons_at_transnet || 0) - (c.wagons_dispatched_to_grindrod || 0))}
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-mono">
                                            {Math.max(0, (c.wagons_at_grindrod || 0) - (c.wagons_released || 0))}
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-mono text-amber-700">
                                            {c.wagons_released || 0}
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-mono font-bold text-emerald-700">
                                            {c.wagons_tipped || 0}
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-mono">{Number(c.tonnage || 0).toLocaleString()}</td>
                                        <td className="px-2 py-1.5">{statusPill(c.status)}</td>
                                        <td className="px-2 py-1.5 text-[10px] font-mono text-slate-500">{fmtDt(c.created_at)}</td>
                                        <td className="px-2 py-1.5 text-[10px] font-mono text-slate-500">{fmtDt(c.actual_completion_at)}</td>
                                        {isAdmin && (
                                            <td className="px-2 py-1.5 text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => remove(c)}
                                                    disabled={busy === c.id}
                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 hover:bg-red-200 disabled:bg-slate-100 text-red-700 text-[10px] font-black uppercase tracking-widest transition"
                                                    data-testid={`rail-list-delete-${c.consignment_no}`}
                                                    title="Delete consignment (admin)"
                                                >
                                                    <Trash size={12} weight="bold" />
                                                    {busy === c.id ? "…" : "Delete"}
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
}
